import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { apiGet } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";
import { DetailSectionsPreview } from "./DetailSectionsPreview";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function getValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return row[key] ?? row[pascalKey];
}

export function ViewServicePage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet(`/api/Services/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        toast.push(String(res.error || "Failed to load service"), "error");
        return;
      }
      setData(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, toast]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Service not found.
      </div>
    );
  }

  const mode = getValue(data, "detailContentMode") === "json" ? "json" : "html";
  const image = resolveMediaUrl(getValue(data, "image"));
  const contentHtml = DOMPurify.sanitize(String(getValue(data, "content") || ""));
  const sectionsJson = getValue(data, "sectionsJson") || "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">{getValue(data, "name")}</h1>
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                mode === "json"
                  ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
                  : "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
              }`}
            >
              {mode === "json" ? "JSON sections" : "HTML content"}
            </span>
          </div>
          {getValue(data, "subtitle") ? (
            <p className="mt-1 text-sm text-slate-600">{getValue(data, "subtitle")}</p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Link
            to={`/services/${id}/edit`}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Edit
          </Link>
          <Link
            to="/services"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back
          </Link>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {image ? (
            <img src={image} alt="" className="aspect-square w-full rounded-xl object-cover" />
          ) : (
            <div className="flex aspect-square items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-400">
              No image
            </div>
          )}
        </div>
        <div className="space-y-4">
          {getValue(data, "shortDescription") ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-600 shadow-sm">
              {getValue(data, "shortDescription")}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-2 px-1">
              <h2 className="text-sm font-semibold text-slate-900">Detail page preview</h2>
              <span className="text-xs text-slate-500">
                Mode: {mode === "json" ? "JSON sections" : "HTML / page builder"}
              </span>
            </div>

            {mode === "json" ? (
              <DetailSectionsPreview sectionsJson={sectionsJson} product={data} />
            ) : contentHtml.trim() ? (
              <div
                className="blog-content overflow-hidden rounded-2xl border border-slate-200 bg-white p-6"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
                No HTML landing content yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
